import { useEffect, useRef, useState } from 'react';

import { getUser } from '@/lib/auth';

// UB5 — verify-waiting polls every 3 s, up to 200 attempts (~10 min).
const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 200;

export type VerifyPollingState = {
  verified: boolean;
  exhausted: boolean;
  attempts: number;
};

export function useVerifyPolling(enabled: boolean): VerifyPollingState {
  const [state, setState] = useState<VerifyPollingState>({
    verified: false,
    exhausted: false,
    attempts: 0,
  });
  const cancelled = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    cancelled.current = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      if (cancelled.current) return;
      attempts += 1;
      try {
        const user = await getUser();
        if (cancelled.current) return;
        if (user?.email_confirmed_at) {
          setState({ verified: true, exhausted: false, attempts });
          return;
        }
      } catch {
        // ignore — keep polling
      }
      if (attempts >= MAX_ATTEMPTS) {
        setState({ verified: false, exhausted: true, attempts });
        return;
      }
      setState({ verified: false, exhausted: false, attempts });
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled]);

  return state;
}
